import type { FunctionComponent, MouseEvent } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { classForDueDate, titleForDueDate } from '@trello/dates';
import { getStringForCombinedDateBadge } from '@trello/dates/i18n';
import { useCardId, useListId } from '@trello/id-context';
import { showFlag } from '@trello/nachos/experimental-flags';
import { CheckboxCheckedIcon } from '@trello/nachos/icons/checkbox-checked';
import { CheckboxUncheckedIcon } from '@trello/nachos/icons/checkbox-unchecked';
import { ClockIcon } from '@trello/nachos/icons/clock';
import type { BadgesTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { Dates } from 'app/scripts/lib/dates';
import { fireCardFrontConfetti } from 'app/src/components/BoardListView/fireCardFrontConfetti';
import { useCanEditBoard } from 'app/src/components/BoardPermissionsContext';
import { useShouldShowCardDoneState } from 'app/src/components/CardDoneState/useShouldShowCardDoneState';
import { CardFrontContext } from 'app/src/components/CardFront/CardFrontContext';
import type { BadgeProps } from './Badge';
import { Badge } from './Badge';
import { useUpdateDueDateCompleteMutation } from './UpdateDueDateCompleteMutation.generated';

const DateEvents = Dates as unknown as {
  on: (event: 'renderInterval', callback: (now: number) => void) => void;
  off: (event: 'renderInterval', callback: (now: number) => void) => void;
};

interface DueDateBadgeProps {
  startDate?: Date;
  dueDate?: Date;
  dueComplete?: boolean;
}

export const DueDateBadge: FunctionComponent<DueDateBadgeProps> = ({
  startDate,
  dueDate,
  dueComplete,
}) => {
  const intl = useIntl();

  const cardId = useCardId();
  const listId = useListId();

  const { cardFrontSource } = useContext(CardFrontContext);
  const shouldShowCardDoneState = useShouldShowCardDoneState(cardFrontSource);

  const [isHovered, setIsHovered] = useState(false);
  const [dueClass, setDueClass] = useState<string | null>(() =>
    dueDate ? classForDueDate(dueDate, dueComplete, Date.now()) : null,
  );

  const canEditBoard = useCanEditBoard();

  const updateDueClass = useCallback(() => {
    setDueClass(
      dueDate ? classForDueDate(dueDate, dueComplete, Date.now()) : null,
    );
  }, [dueComplete, dueDate]);

  /**
   * Periodically update the dueClass variable, so we can keep the color of
   * the badge up to date. But avoid listening to this event if the dueDate
   * has not been set or if it has already been marked as complete.
   */
  useEffect(() => {
    // Immediately update the class when the due date or complete fields change.
    updateDueClass();

    if (dueDate && !dueComplete) {
      DateEvents.on('renderInterval', updateDueClass);
      return () => DateEvents.off('renderInterval', updateDueClass);
    }
  }, [dueComplete, dueDate, updateDueClass]);

  const isInteractive = !shouldShowCardDoneState && !!dueDate && canEditBoard;

  const onMouseEnter = useCallback(() => {
    if (isInteractive) {
      setIsHovered(true);
    }
  }, [isInteractive]);

  const onMouseLeave = useCallback(() => {
    if (isInteractive) {
      setIsHovered(false);
    }
  }, [isInteractive]);

  const [updateDueDateComplete] = useUpdateDueDateCompleteMutation();

  const onClick = useCallback(
    async (e: MouseEvent) => {
      if (!isInteractive) {
        return;
      }

      e.stopPropagation();

      const newDueComplete = !dueComplete;

      const traceId = Analytics.startTask({
        taskName: 'edit-card/dueComplete',
        source: 'cardView',
      });

      try {
        if (newDueComplete) {
          fireCardFrontConfetti({ cardId, listId }, e.currentTarget);
        }

        await updateDueDateComplete({
          variables: {
            traceId,
            cardId,
            dueComplete: newDueComplete,
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateCardDueComplete: {
              id: cardId,
              dueComplete: newDueComplete,
              badges: {
                __typename: 'Card_Badges',
                dueComplete: newDueComplete,
              },
              __typename: 'Card',
            },
          },
        });

        Analytics.sendUpdatedCardFieldEvent({
          field: 'dueComplete',
          source: 'cardView',
          containers: {
            card: { id: cardId },
          },
          attributes: {
            taskId: traceId,
          },
        });

        Analytics.taskSucceeded({
          taskName: 'edit-card/dueComplete',
          traceId,
          source: 'cardView',
        });
      } catch (error) {
        Analytics.taskFailed({
          taskName: 'edit-card/dueComplete',
          traceId,
          source: 'cardView',
          error,
        });
        showFlag({
          id: 'card-front-due-complete',
          title: intl.formatMessage({
            id: 'templates.app_management.something-went-wrong',
            defaultMessage: 'Something went wrong. Please try again later.',
            description: 'Error message when due date completion fails',
          }),
          appearance: 'error',
          isAutoDismiss: true,
        });
      }
    },
    [dueComplete, cardId, intl, isInteractive, listId, updateDueDateComplete],
  );

  const badgeColorProps: Pick<BadgeProps, 'color' | 'isBold'> | null =
    useMemo(() => {
      if (!dueDate) {
        return null;
      }
      if (dueComplete) {
        return { color: 'green', isBold: true };
      }

      switch (dueClass) {
        case 'is-due-soon':
          return { color: 'yellow', isBold: true };
        case 'is-due-past':
          return { color: 'red' };
        case 'is-due-now':
          return { color: 'red', isBold: true };
        default:
          return null;
      }
    }, [dueDate, dueComplete, dueClass]);

  const badgeIcon = useMemo(() => {
    if (!isHovered) {
      return <ClockIcon />;
    }
    return dueComplete ? <CheckboxCheckedIcon /> : <CheckboxUncheckedIcon />;
  }, [isHovered, dueComplete]);

  return (
    <Badge
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      isInteractive={isInteractive}
      role={isInteractive ? 'checkbox' : undefined}
      icon={badgeIcon}
      onClick={onClick}
      title={dueDate ? titleForDueDate(dueDate, dueComplete) : undefined}
      testId={
        dueComplete
          ? getTestId<BadgesTestIds>('badge-due-date-completed')
          : getTestId<BadgesTestIds>('badge-due-date-not-completed')
      }
      {...badgeColorProps}
    >
      {getStringForCombinedDateBadge(startDate, dueDate)}
    </Badge>
  );
};

interface DueDateBadgeWrapperProps {
  due?: string | null;
  start?: string | null;
  dueComplete?: boolean;
  isTemplate?: boolean;
}

// Thin layer around DueDateBadge to avoid excessive rendering.
export const DueDateBadgeWrapper: FunctionComponent<
  DueDateBadgeWrapperProps
> = ({ due, start, dueComplete, isTemplate }) => {
  if ((!due && !start) || isTemplate) {
    return null;
  }
  return (
    <DueDateBadge
      startDate={start ? new Date(start) : undefined}
      dueDate={due ? new Date(due) : undefined}
      dueComplete={dueComplete}
    />
  );
};
